import { useId } from 'react'
import { fieldClasses, floatFieldClasses, floatLabelClasses, floatInputClasses } from './control.js'
/**
 * One labelled form control. Used by the auth and demographics screens.
 * Every input gets a real <label>, a 16px font so iOS does not zoom on focus,
 * and its error wired through aria-describedby.
 */
export function Field({ id, label, hint, error, children }) {
  return (
    <div className="mb-4">
      <label id={`${id}-label`} htmlFor={id} className="mb-1 block text-base font-semibold text-[var(--ink-900)]">
        {label}
      </label>
      {hint ? (
        <p id={`${id}-hint`} className="mb-1 text-sm text-[var(--ink-700)]">
          {hint}
        </p>
      ) : null}
      {children}
      {error ? (
        <p id={`${id}-error`} role="alert" className="mt-1 text-sm text-[var(--critical)]">
          {error}
        </p>
      ) : null}
    </div>
  )
}

export function TextInput({ id, error, hint, className = '', ...props }) {
  const described = [hint ? `${id}-hint` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(' ')
  /*
   * className is MERGED, and pulled out of props on purpose.
   *
   * The spread used to come after className, so any caller passing one silently
   * replaced the field styling wholesale — border, height, padding and the 16px
   * font that stops iOS zooming on focus. A width override would have taken all
   * of that with it, which is a whole-control regression from what reads like a
   * one-word change. Width last so a caller can narrow the field (§3.3's 8em)
   * without losing anything else.
   */
  return (
    <input
      id={id}
      name={id}
      aria-invalid={error ? true : undefined}
      aria-describedby={described || undefined}
      {...props}
      /*
       * disabled MUST reach fieldClasses or a locked control wears a live
       * control's face - normal border, raised fill, black text - which is
       * exactly the deception the derived-age field shipped with: it looked
       * like an input that happened to ignore you. The greyed state already
       * existed in fieldClasses; nothing passed the flag.
       */
      className={'w-full text-base ' + fieldClasses({ invalid: Boolean(error), disabled: Boolean(props.disabled) }) + ' ' + className}
    />
  )
}

export function SelectInput({ id, error, hint, children, ...props }) {
  const described = [hint ? `${id}-hint` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(' ')
  return (
    <select
      id={id}
      name={id}
      aria-invalid={error ? true : undefined}
      aria-describedby={described || undefined}
      className={'w-full text-base ' + fieldClasses({ invalid: Boolean(error) })}
      {...props}
    >
      {children}
    </select>
  )
}


/**
 * FloatField — one control whose label floats. Replaces the Field+TextInput
 * pair wherever the value is a single line of text.
 *
 * THE FLOAT CONDITION IS focus OR value, and both halves matter. Focus alone
 * would drop the label back over the text she just typed the moment she moved
 * on; value alone would leave the label sitting under her cursor while she
 * typed the first character. `value` is read from props so this works for
 * controlled inputs, which is what every caller here uses.
 *
 * A SELECT ALWAYS FLOATS. It has no empty-looking state worth the animation -
 * it shows either a value or a placeholder option - and a label that dropped
 * over a chosen value would obscure the answer. Rest state is for fields she
 * has not reached yet.
 *
 * The <label> stays a real <label htmlFor>, so this is a visual change only:
 * the accessible name, the tap-to-focus target and aria-describedby are
 * exactly what they were before.
 */
export function FloatField({
  id, label, hint, error, value, onChange, as = 'input', children, disabled, ...props
}) {
  const reactId = useId()
  const fid = id || reactId
  const described = [hint ? `${fid}-hint` : null, error ? `${fid}-error` : null]
    .filter(Boolean).join(' ')
  const isSelect = as === 'select'

  /*
   * placeholder=" " IS LOAD-BEARING, NOT A TYPO.
   *
   * `:placeholder-shown` only matches when a placeholder EXISTS, so the float
   * rule needs one present but invisible. A single space is the standard
   * device. Without it the label never rises on a typed value.
   *
   * A select and a date input have no empty-looking state worth animating -
   * a select shows its placeholder option, a date shows mm/dd/yyyy that the
   * browser draws and we cannot suppress - so they float permanently. That was
   * the overlapping "Date of birth / mm/dd/yyyy" defect.
   */
  const alwaysFloat = isSelect
    || ['date', 'time', 'datetime-local', 'month', 'week'].includes(props.type)

  const shared = {
    id: fid,
    name: fid,
    value,
    onChange,
    disabled,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': described || undefined,
    className: floatInputClasses({ disabled }),
  }

  return (
    <div className="mb-4">
      <div className={floatFieldClasses({ invalid: Boolean(error), disabled })}>
        {/* Input FIRST: `peer-` selectors only reach later siblings. */}
        {isSelect
          ? <select {...shared} {...props}>{children}</select>
          : <input placeholder=" " {...shared} {...props} />}
        <label
          htmlFor={fid}
          className={
            floatLabelClasses({ invalid: Boolean(error), disabled })
            + (alwaysFloat ? ' !top-[0.5em] !translate-y-0 !text-[0.75em]' : '')
          }
        >
          {label}
        </label>
      </div>
      {hint ? (
        <p id={`${fid}-hint`} className="mt-1 text-sm text-[var(--ink-700)]">{hint}</p>
      ) : null}
      {/* A BLANK error ( ' ' ) marks the field invalid - border, label colour,
          aria-invalid - without printing a message. Grouped controls use it so
          three date fields can all go red while ONE sentence below the group
          explains, instead of three copies of it. */}
      {error && String(error).trim() ? (
        <p id={`${fid}-error`} role="alert" className="mt-1 text-sm text-[var(--critical)]">{error}</p>
      ) : null}
    </div>
  )
}
